import { formatLargeNumber, iaRa, mString } from 'lib/helpers/utility';

import {
  getColumnMeasureKinds,
  isAdditiveField
} from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';

export const EMPTY_CELL = '-';
export const TOTAL_LABEL = 'Total';

export const NUMBER_FORMATS = {
  currency: '"$"#,##0.00;-"$"#,##0.00',
  count: '#,##0'
};

// Exported cells hold the grid's formatted text: mString for money, formatLargeNumber for counts.
const CURRENCY_PATTERN = /^(-)?\$(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d{1,2}))?$/;
const COUNT_PATTERN = /^-?(\d{1,3}(?:,\d{3})*|\d+)$/;

const parseCurrencyCents = (value) => {
  const match = value.match(CURRENCY_PATTERN);
  if (!match) return null;

  const [, sign, dollars, cents = '0'] = match;
  const amount = Number(dollars.replace(/,/g, '')) * 100 + Number(cents.padEnd(2, '0'));

  return sign ? -amount : amount;
};

const parseCount = (value) => (COUNT_PATTERN.test(value) ? Number(value.replace(/,/g, '')) : null);

// Cents for money, the plain number for counts; null for a blank or anything else.
export const parseMeasure = (kind, value) => {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();

  return kind === 'currency' ? parseCurrencyCents(trimmed) : parseCount(trimmed);
};

export const toSpreadsheetNumber = (kind, measure) =>
  kind === 'currency' ? measure / 100 : measure;

const formatMeasure = (kind, measure) =>
  kind === 'currency' ? mString(measure) : formatLargeNumber(measure);

const getReportTotals = (statusPanelData) =>
  new Map(
    iaRa(statusPanelData)
      .filter(({ value }) => typeof value === 'number' && Number.isFinite(value))
      .map(({ field, value }) => [field, value])
  );

// Which exported headers are report measures, using the grid's own rule (generateColumnDefs), so
// a column is typed and totalled exactly as the report formats it. Detailed exports put the parent
// columns first, then the child columns.
//
// A top-level column the report's status panel also totals takes the panel's figure (cents for
// money, as the cells): top-level rows can overlap, as when a claim billed under three procedure
// codes counts once on each code's row, so their sum would overstate the report. Report grids have
// no filters or paging, so an export always holds every top-level row.
export const describeMeasures = ({
  data,
  headers,
  parentColumns,
  isDetailedMode,
  statusPanelData
}) => {
  const parentKinds = getColumnMeasureKinds(data);
  const rowKinds = isDetailedMode
    ? getColumnMeasureKinds(iaRa(data).flatMap((row) => iaRa(row?.children)))
    : parentKinds;
  const parentCount = isDetailedMode ? parentColumns.length : 0;
  const reportTotals = getReportTotals(statusPanelData);

  return new Map(
    headers
      .map(({ label }, index) => {
        const isParent = index < parentCount;
        const field = isParent ? parentColumns[index].field : headers[index].field;
        const kind = (isParent ? parentKinds : rowKinds).get(field);
        const isTopLevel = isParent || !isDetailedMode;

        return (
          kind && [
            label,
            {
              kind,
              isSummable: isAdditiveField(field),
              reportTotal: isTopLevel ? reportTotals.get(field) : undefined
            }
          ]
        );
      })
      .filter(Boolean)
  );
};

export const sumMeasure = (rows, label, kind) =>
  rows.reduce((sum, row) => sum + (parseMeasure(kind, row[label]) ?? 0), 0);

export const totalMeasure = (rows, label, { kind, reportTotal }) =>
  reportTotal ?? sumMeasure(rows, label, kind);

export const getSummedLabels = (measures) =>
  [...measures].filter(([, { isSummable }]) => isSummable).map(([label]) => label);

// The "Total" caption goes in the first column that holds no measure.
export const getTotalLabelColumn = (labels, measures) =>
  labels.find((label) => !measures.has(label));

// Totals row for CSV, formatted like the cells above it. Null when nothing sums.
export const buildCsvTotalsRow = (rows, labels, measures) => {
  const summed = new Set(getSummedLabels(measures));
  if (!rows.length || !summed.size) return null;

  const labelColumn = getTotalLabelColumn(labels, measures);

  return Object.fromEntries(
    labels.map((label) => {
      if (!summed.has(label)) return [label, label === labelColumn ? TOTAL_LABEL : ''];

      const measure = measures.get(label);

      return [label, formatMeasure(measure.kind, totalMeasure(rows, label, measure))];
    })
  );
};
